import React, { useEffect, useRef, useState } from "react";
import { BannerManager, SiteSectionsCard } from "../../components/site/BannerManager";
import { StoriesManager, FeedAlertsCard, BrandMarkCard } from "../../components/site/FeedTools";
import { PremiumTemplates } from "../../components/growth/GrowthBits";
import { VenuePrivacyCard } from "../../components/VenuePrivacyCard";
import { FocusPicker, focusPos } from "../../components/site/FocusPicker";
import { fileUrl, mediaSrc } from "../../components/site/HeroSlides";
import { toast } from "sonner";
import { BannerStyleCard, SaveTheDateCard } from "../../components/site/BannerExtras";
import { bannerText } from "../../lib/bannerStyle";
import { ExternalLink, Save, ImagePlus, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const TEMPLATES = [
  { id: "luxury-black-tie", name: "Luxury Black Tie" },
  { id: "modern-minimal", name: "Modern Minimal" },
  { id: "romantic-garden", name: "Romantic Garden" },
  { id: "royal-luxury", name: "Royal Luxury" },
];
const STEPS = [["basics", "1. Basics"], ["banner", "2. Banner & cover"], ["story", "3. Story"], ["style", "4. Style"], ["sections", "5. Sections & feed"]];

function LiveBanner({ w, ratio, label, testid, small }) {
  const first = (w.banner_slides || []).find((s) => s.type === "image");
  const src = first ? fileUrl(first.path) : mediaSrc(w.cover_image);
  const focus = first ? first.focus : w.cover_focus;
  const ts = bannerText(w.banner_style);
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`relative ${ratio} rounded-xl overflow-hidden bg-[#111]`} data-testid={testid}>
        {src && <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: focusPos(focus) }} />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/40" />
        <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-3">
          {w.banner_headline && <p className="text-[8px] uppercase tracking-[0.3em] mb-1" style={ts.headline}>{w.banner_headline}</p>}
          <p className={`font-script leading-tight ${small ? "text-base" : "text-2xl"}`} style={ts.names}>{w.couple_names}</p>
          {w.banner_subtitle && <p className="font-serif italic text-[10px] mt-1 opacity-90" style={ts.subtitle}>{w.banner_subtitle}</p>}
          {w.wedding_date && <p className="text-[8px] uppercase tracking-[0.25em] mt-1">{new Date(`${w.wedding_date}T12:00:00`).toLocaleDateString(undefined, small ? { month: "short", day: "numeric", year: "numeric" } : { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>}
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</span>
    </div>
  );
}

function Preview({ w }) {
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5 lg:sticky lg:top-6 order-first lg:order-none" data-testid="site-live-preview">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-3">Live preview</p>
      <div className="flex items-end gap-4 flex-wrap">
        <LiveBanner w={w} ratio="w-64 aspect-[16/9]" label="Computer" testid="preview-wide" />
        <LiveBanner w={w} ratio="w-28 aspect-[9/16]" label="Phone" testid="preview-phone" small />
      </div>
      <p className="text-xs text-[var(--muted)] mt-3">Changes show here right away. Press Save to publish text changes. Photos and focus points save on their own.</p>
    </div>
  );
}

function CoverCard({ w, setW }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  async function upload(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/weddings/mine/cover", fd); setW((x) => ({ ...x, ...data })); toast.success("Cover photo updated"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  async function focus(f) {
    setW((x) => ({ ...x, cover_focus: f }));
    try { await api.patch(`/weddings/${w.wedding_id}`, { cover_focus: f }); } catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className="space-y-3" data-testid="cover-card">
      <p className="text-sm font-medium">Cover photo <span className="text-xs text-[var(--muted)]">· shows when you have no banner slides, and on share previews</span></p>
      <button onClick={() => ref.current?.click()} disabled={busy} className="btn-outline-gold text-xs" data-testid="cover-upload">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />} {w.cover_image ? "Replace cover photo" : "Upload cover photo"}</button>
      <input ref={ref} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="cover-input" />
      {w.cover_image && <FocusPicker src={mediaSrc(w.cover_image)} focus={w.cover_focus} onChange={focus} testid="cover-focus" />}
    </div>
  );
}

export default function WebsiteBuilderPage() {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("basics");
  useEffect(() => { api.get("/weddings/mine").then((r) => setW(r.data)).catch(() => {}); }, []);
  if (!w) return null;
  const set = (patch) => setW({ ...w, ...patch });

  async function save() {
    setBusy(true);
    try {
      const { banner_slides, cover_focus, cover_image, ...rest } = w; // eslint-disable-line no-unused-vars
      const { data } = await api.patch(`/weddings/${w.wedding_id}`, rest);
      setW(data);
      toast.success("Website saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div data-testid="website-builder">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Website Builder</p>
          <h1 className="font-serif font-light text-4xl">Customize your love story</h1>
          <p className="text-sm text-[var(--muted)] mt-2">Public URL: <span className="text-[var(--gold)]">/site/{w.slug}</span></p>
        </div>
        <div className="flex gap-3">
          <a href={`/site/${w.slug}`} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid="view-site"><ExternalLink className="w-4 h-4" /> View Site</a>
          <button onClick={save} disabled={busy} data-testid="save-website" className="btn-gold"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 mb-6" data-testid="builder-steps">
        {STEPS.map(([id, l]) => <button key={id} onClick={() => setStep(id)} data-testid={`builder-step-${id}`} className={`shrink-0 px-4 py-2 rounded-full text-xs transition-colors ${step === id ? "bg-[#111] text-white" : "bg-white border border-[var(--line)] hover:border-[var(--gold)]"}`}>{l}</button>)}
      </div>
      <button onClick={save} disabled={busy} data-testid="save-website-mobile" className="btn-gold fixed bottom-24 left-4 z-40 shadow-lg md:hidden"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start pb-16 md:pb-0">
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8 space-y-5">
          {step === "basics" && <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Partner One" value={w.partner_one} onChange={(v) => set({ partner_one: v })} />
              <Field label="Partner Two" value={w.partner_two} onChange={(v) => set({ partner_two: v })} />
            </div>
            <Field label="Display Name" value={w.couple_names} onChange={(v) => set({ couple_names: v })} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Wedding Date" type="date" value={w.wedding_date || ""} onChange={(v) => set({ wedding_date: v })} />
              <Field label="Hashtag" value={w.hashtag || ""} onChange={(v) => set({ hashtag: v })} placeholder="#ForeverUs" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Venue" value={w.venue || ""} onChange={(v) => set({ venue: v })} />
              <Field label="City" value={w.city || ""} onChange={(v) => set({ city: v })} />
            </div>
            <VenuePrivacyCard w={w} />
            <Field label="Dress Code" value={w.dress_code || ""} onChange={(v) => set({ dress_code: v })} placeholder="Black Tie Optional" />
            <Field label="Travel Info" textarea value={w.travel_info || ""} onChange={(v) => set({ travel_info: v })} />
          </>}
          {step === "banner" && <>
            <Field label="Banner headline (small text above your names)" value={w.banner_headline || ""} onChange={(v) => set({ banner_headline: v })} placeholder="We're getting married" max={80} testid="banner-headline" />
            <Field label="Banner subtitle (under your names)" value={w.banner_subtitle || ""} onChange={(v) => set({ banner_subtitle: v })} placeholder="Join us for a weekend of love in Napa" max={160} testid="banner-subtitle" />
            <BannerStyleCard w={w} set={set} />
            <BannerManager w={w} onChange={(p) => setW((x) => ({ ...x, ...p }))} />
            <CoverCard w={w} setW={setW} />
            <SaveTheDateCard />
          </>}
          {step === "story" && <>
            <Field label="How we met" textarea value={w.story || ""} onChange={(v) => set({ story: v })} placeholder="Two hearts, one love…" />
            <Field label="The proposal" textarea value={w.proposal_story || ""} onChange={(v) => set({ proposal_story: v })} />
          </>}
          {step === "style" && <>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block">Template</label>
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => <button key={t.id} onClick={() => set({ template: t.id })} data-testid={`template-${t.id}`} className={`py-3 rounded-xl text-xs uppercase tracking-wider ${w.template === t.id ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] hover:border-[var(--gold)]"}`}>{t.name}</button>)}
            </div>
            <PremiumTemplates current={w.template} weddingId={w.wedding_id} onPick={(id) => set({ template: id })} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!w.rsvp_open} onChange={(e) => set({ rsvp_open: e.target.checked })} data-testid="rsvp-open-toggle" /> Allow guests to RSVP from your public site</label>
          </>}
          {step === "sections" && <>
            <SiteSectionsCard w={w} onChange={(p) => setW((x) => ({ ...x, ...p }))} />
            <StoriesManager />
            <FeedAlertsCard />
            <BrandMarkCard />
          </>}
          <div className="flex justify-between pt-2">
            {STEPS.findIndex(([id]) => id === step) > 0 ? <button onClick={() => setStep(STEPS[STEPS.findIndex(([id]) => id === step) - 1][0])} className="text-xs underline" data-testid="builder-prev">Back</button> : <span />}
            {STEPS.findIndex(([id]) => id === step) < STEPS.length - 1 && <button onClick={() => setStep(STEPS[STEPS.findIndex(([id]) => id === step) + 1][0])} className="btn-outline-gold text-xs" data-testid="builder-next">Next step</button>}
          </div>
        </div>
        <Preview w={w} />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, placeholder, max, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</label>
      {textarea ? (
        <textarea value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={3} className="input-luxe resize-none" data-testid={testid} />
      ) : (
        <input type={type} placeholder={placeholder} value={value} maxLength={max} onChange={(e) => onChange(e.target.value)} className="input-luxe" data-testid={testid} />
      )}
    </div>
  );
}
